import React, { useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useGet } from "../../api";
import { FilterBar, FilterField } from "../../components/ui";
import { IWorkPipelineListTemplateReport } from "../../interfaces";

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** Local-midnight bounds, as the Productivity page sends them. */
const rangeParams = (from: string, to: string) => {
  const p = new URLSearchParams();
  if (from) p.set("dateFrom", new Date(`${from}T00:00:00`).toISOString());
  if (to) p.set("dateTo", new Date(`${to}T23:59:59.999`).toISOString());
  return p;
};

/** Per QA list and per tester: live templates, blocks by reason and what's still open. */
export const ListTemplateReport: React.FC<{ qaListId: string }> = ({ qaListId }) => {
  const defaultFrom = isoDay(new Date(Date.now() - 29 * 864e5));
  const defaultTo = isoDay(new Date());
  const [draft, setDraft] = useState({ from: defaultFrom, to: defaultTo });
  const [range, setRange] = useState(draft);

  const url = useMemo(() => {
    const p = rangeParams(range.from, range.to);
    if (qaListId) p.set("qaListId", qaListId);
    return `work-pipeline/report/lists?${p.toString()}`;
  }, [range, qaListId]);
  const { data, loading } = useGet<IWorkPipelineListTemplateReport>(url, true);

  return (
    <Card className="mt-3">
      <Card.Header className="py-2 small fw-bold">Templates by QA list</Card.Header>
      <Card.Body>
        <FilterBar
          onSearch={() => setRange(draft)}
          onClear={() => {
            setDraft({ from: defaultFrom, to: defaultTo });
            setRange({ from: defaultFrom, to: defaultTo });
          }}
          activeCount={range.from !== defaultFrom || range.to !== defaultTo ? 1 : 0}
        >
          <FilterField label="From" md={3} tooltip="Start of the range, local time.">
            <Form.Control
              size="sm"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </FilterField>
          <FilterField label="To" md={3} tooltip="End of the range, inclusive.">
            <Form.Control
              size="sm"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </FilterField>
        </FilterBar>

        <div className="small text-muted mb-2">
          Live means an active auto template in SQL for the domain right now (what the Templates
          page counts as live in SQL). Went live counts templates synced to SQL in the range. Handled
          and blocked come from board actions in the range; blocked includes a can't-test brand
          status. Open is neither live, blocked nor complete. The QA list filter above applies.
        </div>

        {data && !data.liveKnown && (
          <Alert variant="warning" className="py-2 small">
            SQL couldn't be read, so live counts are missing, not zero.
          </Alert>
        )}

        {loading && !data ? (
          <div className="text-center p-3">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : !data ? null : (
          <Row className="g-3">
            <Col lg={8}>
              <div style={{ overflowX: "auto" }}>
                <Table size="sm" hover className="mb-0 small align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>List</th>
                      <th className="text-end" title="Domain + country on the list">Domains</th>
                      <th className="text-end" title="Worked by a person in the range">Handled</th>
                      <th className="text-end" title="Active auto template in SQL now">Live</th>
                      <th className="text-end" title="Synced to SQL in the range">Went live</th>
                      <th>Blocked</th>
                      <th className="text-end" title="Not live, not blocked, not complete">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lists.map((l) => (
                      <tr key={l.qaListId}>
                        <td className="fw-semibold">
                          {l.qaListName}
                          {l.systemKey && (
                            <Badge bg="light" text="dark" className="ms-1 border" title="Filled by the system">
                              system
                            </Badge>
                          )}
                        </td>
                        <td className="text-end">{l.total}</td>
                        <td className="text-end">{l.handled}</td>
                        <td className="text-end">{data.liveKnown ? l.live : "—"}</td>
                        <td className="text-end">{l.wentLive}</td>
                        <td>
                          {l.blocked ? (
                            <span title={l.blockedByReason.map((r) => `${r.reason}: ${r.count}`).join("\n")}>
                              {l.blocked}{" "}
                              <span className="text-muted">
                                ({l.blockedByReason.map((r) => `${r.reason} ${r.count}`).join(", ")})
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-end">{l.open}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col lg={4}>
              <Table size="sm" hover className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>Tester</th>
                    <th className="text-end" title="Domains they worked in the range">Handled</th>
                    <th className="text-end" title="Of those, live now">Live</th>
                    <th className="text-end" title="Blocks proposed or can't-test statuses set">Blocked</th>
                  </tr>
                </thead>
                <tbody>
                  {data.testers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-muted">
                        Nobody worked these lists in the range.
                      </td>
                    </tr>
                  ) : (
                    data.testers.map((t) => (
                      <tr key={t.actor}>
                        <td>{t.actorName}</td>
                        <td className="text-end">{t.handled}</td>
                        <td className="text-end">{data.liveKnown ? t.live : "—"}</td>
                        <td className="text-end">{t.blocked}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};
